/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { ReferenceType } from '@/shared/components/DynamicForm/DynamicForm'
import { useSelectedLanguagesQuery } from '../hooks/useSelectedLanguagesQuery'

interface EditLanguageFormConfigProps {
  handleIsDirtyChange: (isDirty: boolean) => void
  allLanguagesList: OptionType[]
  defaultValues: {
    defaultLanguageName: OptionType
    selectedLanguagesName: OptionType[]
  }
}

export function useLanguageFormConfig(props: EditLanguageFormConfigProps) {
  const { handleIsDirtyChange, allLanguagesList, defaultValues } = props
  const { t } = useTranslation()

  const form = useForm({
    defaultValues
  })
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { selectedLanguagesOptions, putLanguages } =
    useSelectedLanguagesQuery(currentLanguage)

  const [defaultLanguageOptions, setDefaultLanguageOptions] = useState<
    OptionType[]
  >(selectedLanguagesOptions)

  useEffect(() => {
    setDefaultLanguageOptions(selectedLanguagesOptions)
    form.reset(defaultValues)
  }, [defaultValues, form, selectedLanguagesOptions])

  const formConfig = {
    id: 'edit-language-form',
    fields: [
      {
        name: 'defaultLanguageName',
        label: t('LANGUAGE.DEFAULT_LANGUAGE'),
        type: 'select',
        focused: true,
        placeholder: t('LANGUAGE.DEFAULT_LANGUAGE'),
        options: defaultLanguageOptions,
        referenceField: 'selectedLanguagesName',
        referenceTypes: ['watchOptions'] as ReferenceType[],
        hideClearIcon: true,
        required: true
      },
      {
        name: 'selectedLanguagesName',
        label: t('LANGUAGE.NAME'),
        placeholder: t('LANGUAGE.NAME'),
        type: 'multi-select',
        options: allLanguagesList,
        required: true,
        // The accordion is a narrow column, and the row scrolls sideways, so the
        // second chip was left sliced in half by the field's edge and read as
        // broken rather than as something to scroll.
        fitTags: true,
        /*
          The default language cannot be taken out of the list. The payload
          marks whichever entry equals `defaultLanguageName` as `isDefault`, so
          without it nothing is sent as default and the save silently produces a
          restaurant with no main language. This also survives clearing the
          field outright, which is the way it was easiest to get into that state.
        */
        referenceField: 'defaultLanguageName',
        referenceTypes: ['lockReferenceValue'] as ReferenceType[]
      }
    ],
    defaultValues,

    onSubmit: (data: FieldValues) => {
      const {
        defaultLanguageName,
        selectedLanguagesName: selectedLanguagesNames
      } = data

      const mappedLanguagesNames = selectedLanguagesNames.map(
        (language: OptionType) =>
          language.value === defaultLanguageName.value
            ? { languageCode: language.value, isDefault: true }
            : { languageCode: language.value }
      )

      putLanguages(mappedLanguagesNames)
    },
    onIsDirtyChange: ((isDirty: boolean) => {
      const { defaultLanguageName, selectedLanguagesName } = form.getValues()

      if (defaultLanguageName && selectedLanguagesName) {
        handleIsDirtyChange(isDirty)
      }
    }) as Dispatch<SetStateAction<boolean>>
  }

  return formConfig
}
